import React, { useState } from "react";
import { UploadDropZone } from "./dropzone";
import { Typography, Box, IconButton } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { UploadDropZoneProps } from "./utils";
import { StyledButton } from "../buttons/custom-button";
import { LoaderComponent } from "../loaders/page-loader";
import { useApi } from "../../hooks/useApi";
import { ReactComponent as Loader } from "../../../assests/logos/loader.svg";

interface FileUploaderProps {
  uniqueId: string;
  setFileName: (fileName: string[]) => void;
  setFilePath: (filePath: string[]) => void;
  onUploadSuccess?: (fileInfo: { fileName: string; file: File }) => void;
  onUploadError?: (error: string) => void;
}

const FileUploader = ({ 
  uniqueId, 
  setFileName, 
  setFilePath, 
  onUploadSuccess, 
  onUploadError 
}: FileUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>("");
  const { executePut } = useApi();

  // Validate file type (Excel only)
  const validateFileType = (file: File): boolean => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setError("Please upload only Excel (.xlsx) files");
      return false;
    }
    return true;
  };

  // Upload file to backend using executePut
  const uploadFileToBackend = async (file: File): Promise<boolean> => {
    try {
      const uploadUrl = `v1/ml/upload-excel?filename=${encodeURIComponent(file.name)}`;
      
      const config = {
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'Cache-Control': 'no-cache',
          'Accept': '*/*'
        }
      };

      const response = await executePut(
        uploadUrl,
        file, // File object as data
        config,
        'https://apim-te-sms-etrkit-eastus-shared-01.azure-api.net' // baseUrlOverride
      );
      
      if (response?.status === 200) {
        return true;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('File upload error:', error);
      return false;
    }
  };

  const handleFileUpload: UploadDropZoneProps["onClick"] = (event) => {
    const { files: rawFiles, error: fileError } = event.target;

    if (fileError) {
      setError(fileError);
      return;
    }

    const files = rawFiles as FileList;
    if (files && files.length > 0) {
      const file = files[0]; // Only take first file
      
      if (validateFileType(file)) {
        setSelectedFile(file);
        setError("");
      }
    }
  };

  const handleUploadClick = async () => {
    if (!selectedFile) {
      setError("No file selected for upload.");
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const uploadSuccess = await uploadFileToBackend(selectedFile);
      
      if (uploadSuccess) {
        // Update parent component
        setFileName([selectedFile.name]);
        setFilePath([selectedFile.name]);
        
        // Call success callback
        if (onUploadSuccess) {
          onUploadSuccess({
            fileName: selectedFile.name,
            file: selectedFile
          });
        }
      } else {
        setError("File upload failed");
        if (onUploadError) {
          onUploadError("File upload failed");
        }
      }
    } catch (error) {
      const errorMessage = `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setError(errorMessage);
      if (onUploadError) {
        onUploadError(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError("");
    // Reset the file input by finding it in the DOM
    const fileInput = document.querySelector(`#${uniqueId}-upload-zone input[type="file"]`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto", padding: "16px" }}>
      {isUploading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '300px',
          flexDirection: 'column'
        }}>
          <Loader style={{ width: 50, height: 50 }} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Uploading file...
          </Typography>
        </Box>
      ) : (
        <>
          <UploadDropZone
            onClick={handleFileUpload}
            label="Upload your file"
            id="upload-zone"
            dataTestId="file-uploader-dropzone"
            acceptFiles={["xlsx"]}
            maxSize="10MB"
            multiple={false}
            fileRange={1}
            allowDuplication={false}
            acceptMultiDot={true}
            error={Boolean(error)}
            removeText="file"
          />

          {error && (
            <Typography variant="body2" color="error" style={{ marginTop: "16px" }}>
              {error}
            </Typography>
          )}

          <div style={{ marginTop: "24px" }}>
            {selectedFile && (
              <Typography variant="h6">Selected File:</Typography>
            )}
            {selectedFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Typography variant="body2">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  disabled={isUploading}
                  sx={{
                    color: '#d32f2f',
                    '&:hover': {
                      backgroundColor: '#ffebee',
                      color: '#b71c1c'
                    },
                    '&:disabled': {
                      color: '#bdbdbd'
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </div>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleUploadClick}
              disabled={!selectedFile}
            >
              Upload Files
            </StyledButton>
          </Box>
        </>
      )}
    </div>
  );
};

export default FileUploader;